/**
 * XAES Analyst Agent info window — opens from the XAES-AI button in
 * HudHeader. Ported from `_legacy/mc.html` lines 1740-1880 (MC_AGENT),
 * restricted to the LEFT column (About / Role / Capabilities / Model).
 *
 * Legacy had a second column with a chat panel. We don't reproduce that
 * because the React build mounts `<XaesAnalyst>` permanently in the
 * page-side sidebar — a duplicate chat surface would compete for the
 * same idb-keyval persistence key. The footer line points users at the
 * always-on sidebar instead.
 *
 * Visual matches `_legacy/xaes.css` `.mc-panel-glow` chrome + the legacy
 * 3-column title bar (empty 1fr | centred title block | right cluster).
 */

import { useLocation } from "react-router-dom";
import wdBg from "@/assets/wd-bg.jpg";
import { Bot, MessageSquare, PlayCircle, Search, ShieldCheck, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { WORKSHOPS } from "@/content/workshops";
import { XaesAnalyst } from "@/features/xaes-analyst/XaesAnalyst";
import type { ChatScope } from "@/features/xaes-analyst/context-builder";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Capability {
  icon: React.ReactNode;
  accent: string;       // var(--accent-x)
  accentRgb: string;    // var(--accent-x-rgb)
  title: string;
  body: string;
}

const CAPABILITIES: Capability[] = [
  {
    icon: <PlayCircle className="h-4 w-4" aria-hidden />,
    accent: "var(--accent-true-blue)",
    accentRgb: "var(--accent-true-blue-rgb)",
    title: "Workshop Navigation",
    body: "Explains activities, objectives, and programme structure across all WS sessions.",
  },
  {
    icon: <Search className="h-4 w-4" aria-hidden />,
    accent: "var(--accent-green)",
    accentRgb: "var(--accent-green-rgb)",
    title: "Real-Time Analysis",
    body: "Synthesises session data, crew insights, and organisational frameworks on demand.",
  },
  {
    icon: <MessageSquare className="h-4 w-4" aria-hidden />,
    accent: "var(--accent-melon)",
    accentRgb: "var(--accent-melon-rgb)",
    title: "Facilitator Support",
    body: "Answers participant questions, drafts summaries, and surfaces key themes in real time.",
  },
  {
    icon: <ShieldCheck className="h-4 w-4" aria-hidden />,
    accent: "var(--accent-gold)",
    accentRgb: "var(--accent-gold-rgb)",
    title: "Enterprise Intelligence",
    body: "Deep knowledge of the organisation and enterprise concepts, models, and evaluation criteria.",
  },
];

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div
        className="font-mono font-bold tracking-[0.25em] uppercase"
        style={{
          color: "var(--accent-sky)",
          fontSize: "var(--fs-label)",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        className="font-mono leading-relaxed"
        style={{
          color: "var(--text-secondary)",
          fontSize: "var(--fs-sub)",
          lineHeight: 1.7,
          marginBottom: 22,
        }}
      >
        {children}
      </div>
    </>
  );
}

function useActiveChatScope(): ChatScope {
  const { pathname } = useLocation();
  const match = /^\/workshop\/([^/]+)$/.exec(pathname);
  const id = match?.[1] ?? "";
  const ws = WORKSHOPS.find((w) => w.id === id);
  return ws?.id ?? "MC";
}

export function XaesAgentModal({ open, onClose }: Props) {
  const scope = useActiveChatScope();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          // Legacy `_legacy/mc.html` line 1741 sized `MC_AGENT` with
          // `position:absolute; inset:0; padding:20px 80px 140px 80px`
          // so the agent panel fills the MC body between the HUD
          // header (56 px) and bottom-nav (112 px). Force both width
          // AND height so the dialog doesn't auto-collapse to content.
          width: "calc(var(--os-vw) * 100 - 32px)",
          maxWidth: "calc(var(--os-vw) * 100 - 32px)",
          height: "calc(var(--os-vh) * 100 - 56px - 112px - 32px)",
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 32px)",
        }}
      >
        <DialogTitle className="sr-only">XAES-AI</DialogTitle>
        <DialogDescription className="sr-only">
          XAES Analyst Agent info — about, role, capabilities, and the model powering this build.
        </DialogDescription>
        <div
          // Same `panel-glow-mc` chrome as the MC main body and the
          // MeetCrew modal — gives the agent modal the same "main box"
          // framing instead of a separately-styled floating dialog.
          className="panel-glow-mc flex h-full w-full flex-col overflow-hidden backdrop-blur"
          style={{ background: "var(--bg-panel-alt)" }}
        >
          {/* Title bar — legacy 3-column: empty 1fr / centred title / right cluster */}
          <div
            className="flex flex-shrink-0 items-center gap-3 px-4 py-3"
            style={{ borderBottom: "1px solid hsl(var(--border))" }}
          >
            <div className="flex-1" />
            <div className="flex items-center gap-2.5">
              <Bot
                className="h-4 w-4 flex-shrink-0"
                style={{ color: "var(--accent-sky)" }}
                aria-hidden
              />
              <p
                className="font-mono font-bold tracking-[0.35em] uppercase"
                style={{ color: "var(--text-primary)", fontSize: "var(--fs-heading)" }}
              >
                XAES-AI
              </p>
              <span
                className="rounded-md px-2 py-px font-mono font-semibold tracking-wider"
                style={{
                  color: "var(--accent-sky)",
                  border: "1px solid var(--accent-sky)",
                  background: "rgba(var(--accent-sky-rgb), 0.1)",
                  fontSize: "var(--fs-label)",
                }}
              >
                ANALYST AGENT
              </span>
            </div>
            <div className="flex flex-1 items-center justify-end gap-2">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                className="font-mono tracking-[0.2em] uppercase"
                style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)", marginRight: 8 }}
              >
                Online
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex items-center gap-1 rounded-md border px-2 py-1 font-mono font-semibold tracking-widest uppercase transition-colors"
                style={{
                  fontSize: "var(--fs-base)",
                  color: "var(--text-secondary)",
                  borderColor: "hsl(var(--border))",
                  background: "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--accent-true-blue)";
                  e.currentTarget.style.borderColor = "var(--accent-true-blue)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "var(--text-secondary)";
                  e.currentTarget.style.borderColor = "hsl(var(--border))";
                }}
              >
                <X aria-hidden style={{ width: 11, height: 11 }} />
                Close
              </button>
            </div>
          </div>

          {/* Body — legacy MC_AGENT is a 2-col grid (1fr / 1.1fr), left is
              the agent profile, right is the chat. We mirror that. The
              page-side sidebar `<XaesAnalyst>` is hidden via the
              `useXaesAgentModal` store while this modal is open so the
              two mounts don't race on idb-keyval persistence. */}
          <div
            className="min-h-0 flex-1 overflow-hidden"
            style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr" }}
          >
            {/* LEFT — About panel (scrollable) */}
            <div
              className="min-h-0 overflow-y-auto px-6 py-6"
              style={{ borderRight: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)" }}
            >
            {/* Avatar + name — same wd-bg.jpg crop the vanilla MC_AGENT
                modal uses (`_legacy/mc.html` line 1768). */}
            <div className="mb-6 flex items-center gap-4">
              <div
                aria-hidden
                className="flex-shrink-0 overflow-hidden rounded-full"
                style={{
                  width: 54,
                  height: 54,
                  border: "2px solid var(--accent-sky)",
                  boxShadow: "0 0 18px rgba(var(--accent-sky-rgb), 0.25)",
                }}
              >
                <img
                  src={wdBg}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "60% center",
                  }}
                />
              </div>
              <div>
                <div
                  className="font-mono font-bold tracking-[0.12em] uppercase"
                  style={{ color: "var(--text-primary)", fontSize: "var(--fs-title)" }}
                >
                  XAES Analyst Agent
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span
                    aria-hidden
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "var(--accent-green)",
                      boxShadow: "var(--glow-green)",
                      animation: "pulse 2s infinite",
                    }}
                  />
                  <span
                    className="font-mono tracking-wider"
                    style={{ color: "var(--accent-green)", fontSize: "var(--fs-base)" }}
                  >
                    ONLINE
                  </span>
                  <span
                    className="font-mono tracking-wide"
                    style={{ color: "var(--text-muted)", fontSize: "var(--fs-base)", marginLeft: 4 }}
                  >
                    · Mission Control AI
                  </span>
                </div>
              </div>
            </div>

            <Section label="XAES-AI">
              XAES Analyst Agent (XAES-AI) is the embedded AI intelligence layer for Mission
              Control. Trained on XAES Phase Zero's full programme architecture, it supports
              facilitators and participants with real-time analysis, contextual guidance, and
              workshop intelligence — directly within the session environment.
            </Section>

            <Section label="Role">
              Acts as a real-time analyst and co-pilot during work-stream sessions. Bridges the
              gap between workshop activities and actionable AI insight — helping the crew move
              faster, think clearer, and deliver sharper outcomes.
            </Section>

            <div
              className="font-mono font-bold tracking-[0.25em] uppercase"
              style={{
                color: "var(--accent-sky)",
                fontSize: "var(--fs-label)",
                marginBottom: 12,
              }}
            >
              Capabilities
            </div>
            <div className="mb-6 flex flex-col gap-2">
              {CAPABILITIES.map((cap) => (
                <div
                  key={cap.title}
                  className="flex items-start gap-3 rounded-lg px-3 py-2.5"
                  style={{
                    background: `rgba(${cap.accentRgb}, 0.05)`,
                    border: `1px solid rgba(${cap.accentRgb}, 0.15)`,
                  }}
                >
                  <div style={{ color: cap.accent, marginTop: 2 }}>{cap.icon}</div>
                  <div>
                    <div
                      className="font-mono font-semibold tracking-wider"
                      style={{
                        color: "var(--text-primary)",
                        fontSize: "var(--fs-base)",
                        marginBottom: 2,
                      }}
                    >
                      {cap.title}
                    </div>
                    <div
                      className="font-mono leading-relaxed"
                      style={{ color: "var(--text-muted)", fontSize: "var(--fs-base)", lineHeight: 1.5 }}
                    >
                      {cap.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Model tag */}
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-2"
              style={{
                background: "rgba(var(--accent-sky-rgb), 0.04)",
                border: "1px solid rgba(var(--accent-sky-rgb), 0.12)",
              }}
            >
              <Bot className="h-3 w-3" style={{ color: "var(--accent-sky)" }} aria-hidden />
              <span
                className="font-mono tracking-wider"
                style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
              >
                Powered by
              </span>
              <span
                className="font-mono font-semibold tracking-wider"
                style={{ color: "var(--accent-sky)", fontSize: "var(--fs-label)" }}
              >
                Claude · Haiku 4.5
              </span>
              <span
                className="ml-auto font-mono tracking-wider"
                style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
              >
                XAES // PHASE ZERO
              </span>
            </div>

            </div>

            {/* RIGHT — Live chat. The sidebar mount on MC + WD pages is
                hidden while the modal is open (see store comment above). */}
            <div className="flex min-h-0 flex-col">
              <XaesAnalyst workshopId={scope} className="flex-1 rounded-none border-0" />
            </div>
          </div>

          {/* Footer bar — mirrors legacy MC_AGENT bar-btm */}
          <div
            className="flex flex-shrink-0 items-center gap-2 px-4 py-2"
            style={{ borderTop: "1px solid hsl(var(--border))" }}
          >
            <span
              className="font-mono tracking-[0.25em] uppercase"
              style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
            >
              XAES // PHASE ZERO
            </span>
            <span
              aria-hidden
              style={{
                fontSize: "var(--fs-label)",
                color: "rgba(var(--accent-true-blue-rgb), 0.4)",
              }}
            >
              ·
            </span>
            <span
              className="font-mono tracking-wider"
              style={{ color: "var(--accent-true-blue)", fontSize: "var(--fs-label)" }}
            >
              Mission Control Agent
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
